import { useCallback, useEffect, useRef, useState } from "react";
import { CalendarEventModel } from "../../../data/entities";

export type ClipboardMode = "copy" | "cut";

export interface CopiedAppointment {
  customerFirstName: string;
  customerLastName: string;
  email: string;
  phoneNumber: string;
  locationId: number;
  providerId: number;
  serviceId: number;
  notes?: string;
  durationMs: number;
  label: string;
}

export interface ParkedAppointment extends CopiedAppointment {
  mode: ClipboardMode;
  /** Set for "cut" only — the appointment being moved. */
  bookingEventId?: number;
  /** Snapshot taken when the appointment was picked up, so the move survives a cache refresh. */
  sourceEvent?: CalendarEventModel;
  /** Where it came from, e.g. "Mon, Jul 27, 2:00 PM". */
  originalLabel?: string;
}

export interface AppointmentMenuState {
  mode: "item" | "slot";
  offset: { left: number; top: number };
  dataItem?: any;
  slotStart?: Date;
  slotProviderId?: number;
}

export interface AppointmentClipboardHandlers {
  /** Creates a new appointment from a copied one. */
  onPasteAppointment: (item: any) => void;
  /** Moves the existing appointment to the target slot. */
  onMoveAppointment: (
    parked: ParkedAppointment,
    target: { start: Date; end: Date; providerId?: number }
  ) => void;
  /** Resolves the full event for an appointment being cut. */
  findCalendarEvent: (bookingEventId: number) => CalendarEventModel | undefined;
}

const formatOrigin = (start: Date) =>
  start.toLocaleString(undefined, {
    weekday: "short",
    month: "short",
    day: "numeric",
    hour: "numeric",
    minute: "2-digit",
  });

export function useAppointmentClipboard(handlers: AppointmentClipboardHandlers) {
  const [parked, setParked] = useState<ParkedAppointment | null>(null);
  const [menu, setMenu] = useState<AppointmentMenuState | null>(null);
  const menuRef = useRef<HTMLDivElement | null>(null);

  const handlersRef = useRef(handlers);
  handlersRef.current = handlers;

  const closeMenu = useCallback(() => setMenu(null), []);

  useEffect(() => {
    if (!menu) return;

    const handleMouseDown = (e: MouseEvent) => {
      if (menuRef.current?.contains(e.target as Node)) return;
      closeMenu();
    };
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") closeMenu();
    };

    document.addEventListener("mousedown", handleMouseDown);
    document.addEventListener("keydown", handleKeyDown);
    document.addEventListener("wheel", handleMouseDown as any);
    return () => {
      document.removeEventListener("mousedown", handleMouseDown);
      document.removeEventListener("keydown", handleKeyDown);
      document.removeEventListener("wheel", handleMouseDown as any);
    };
  }, [menu, closeMenu]);

  // Esc puts a parked appointment back when no menu is open.
  useEffect(() => {
    if (!parked || menu) return;

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") setParked(null);
    };
    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [parked, menu]);

  const openItemMenu = useCallback((e: React.MouseEvent, dataItem: any) => {
    e.preventDefault();
    e.stopPropagation();
    setMenu({
      mode: "item",
      offset: { left: e.pageX, top: e.pageY },
      dataItem,
    });
  }, []);

  const openSlotMenu = useCallback((e: React.MouseEvent, slot: { start: Date; providerId?: number }) => {
    e.preventDefault();
    e.stopPropagation();
    setMenu({
      mode: "slot",
      offset: { left: e.pageX, top: e.pageY },
      slotStart: slot.start,
      slotProviderId: slot.providerId,
    });
  }, []);

  const buildParked = (dataItem: any, mode: ClipboardMode): ParkedAppointment => {
    const start = new Date(dataItem.start);
    const end = new Date(dataItem.end);

    return {
      mode,
      customerFirstName: dataItem.customerFirstName ?? "",
      customerLastName: dataItem.customerLastName ?? "",
      email: dataItem.email ?? "",
      phoneNumber: dataItem.phoneNumber ?? "",
      locationId: dataItem.locationId,
      providerId: dataItem.providerId,
      serviceId: dataItem.serviceId,
      notes: dataItem.notes,
      durationMs: end.getTime() - start.getTime(),
      label: [dataItem.customerName, dataItem.title].filter(Boolean).join(" - "),
    };
  };

  const copyFromMenu = useCallback(() => {
    const dataItem = menu?.dataItem;
    if (!dataItem) return;

    setParked(buildParked(dataItem, "copy"));
    closeMenu();
  }, [menu, closeMenu]);

  const cutFromMenu = useCallback(() => {
    const dataItem = menu?.dataItem;
    if (!dataItem) return;

    // Snapshot now, while the event is guaranteed to still be in state.
    const sourceEvent = handlersRef.current.findCalendarEvent(dataItem.bookingEventId);
    if (!sourceEvent) return;

    setParked({
      ...buildParked(dataItem, "cut"),
      bookingEventId: dataItem.bookingEventId,
      sourceEvent,
      originalLabel: formatOrigin(new Date(dataItem.start)),
    });
    closeMenu();
  }, [menu, closeMenu]);

  const placeFromMenu = useCallback(() => {
    if (!parked || !menu?.slotStart) return;

    const start = new Date(menu.slotStart);
    const end = new Date(start.getTime() + parked.durationMs);
    const providerId = menu.slotProviderId ?? parked.providerId;

    if (parked.mode === "cut") {
      handlersRef.current.onMoveAppointment(parked, { start, end, providerId });
    } else {
      handlersRef.current.onPasteAppointment({
        customerFirstName: parked.customerFirstName,
        customerLastName: parked.customerLastName,
        email: parked.email,
        phoneNumber: parked.phoneNumber,
        locationId: parked.locationId,
        providerId,
        serviceId: parked.serviceId,
        notes: parked.notes,
        start,
        end,
      });
    }

    setParked(null);
    closeMenu();
  }, [parked, menu, closeMenu]);

  const clearParked = useCallback(() => setParked(null), []);

  return {
    parked,
    menu,
    menuRef,
    openItemMenu,
    openSlotMenu,
    copyFromMenu,
    cutFromMenu,
    placeFromMenu,
    clearParked,
    closeMenu,
  };
}
